import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { formatData, sendHipaaForm } from '../services/FormService';

/**
 * Shared hook for HIPAA form handling
 * Reduces code duplication between form components
 *
 * @param {string} formType - Type of form (e.g., 'appointment', 'contact-us')
 * @param {object} formSettings - Form configuration with fields and settings
 * @returns {object} Form state and handlers
 */
export function useHipaaForm(formType, formSettings) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset
  } = useForm({ mode: 'onBlur' });

  /**
   * Gets the appropriate value for a field based on its type
   */
  const setValue = useCallback((field, value) => {
    if (field.type === 'select') {
      return field.options?.find((c) => c.value === value);
    }
    return value;
  }, []);

  /**
   * Validates a single field
   */
  const validateField = useCallback((field, value) => {
    if (!field.isRequired) {
      return true;
    }

    // For select fields, check if value exists
    if (field.type === 'select') {
      return !!value;
    }

    // For other fields, check if the trimmed string is non-empty
    const stringValue = value?.toString().trim();
    return !!stringValue;
  }, []);

  /**
   * Handles form submission
   */
  const onSubmit = useCallback(
    async (data) => {
      setIsSubmitting(true);
      setError(null);

      try {
        // Validate all required fields
        const validationErrors = {};
        formSettings.fields.forEach((field) => {
          const value = data[field.key];
          if (!validateField(field, value)) {
            validationErrors[field.key] = `${field.name} is required`;
          }
        });

        if (Object.keys(validationErrors).length > 0) {
          setError('Please fill in all required fields');
          setIsSubmitting(false);
          return;
        }

        // Execute reCAPTCHA
        if (!executeRecaptcha) {
          throw new Error('ReCAPTCHA not available. Please try again.');
        }

        // Sanitize action name: reCAPTCHA only allows A-Za-z/_
        const actionName = formType.replace(/-/g, '_');
        const token = await executeRecaptcha(actionName);
        if (!token) {
          throw new Error('ReCAPTCHA verification failed. Please try again.');
        }

        // Format and send data
        const formattedData = formatData(data, formSettings);
        const response = await sendHipaaForm(formType, formattedData, token);

        // Handle success
        if (response?.status) {
          setIsSuccess(true);
          reset();
          // Scroll to top to show success message
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err) {
        const errorMessage =
          err.message || 'An unexpected error occurred. Please try again later.';
        setError(errorMessage);

        // Log error in development
        if (process.env.NODE_ENV === 'development') {
          console.error('Form submission error:', err);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [executeRecaptcha, formType, formSettings, reset, validateField]
  );

  /**
   * Resets the form and clears success state
   */
  const resetForm = useCallback(() => {
    setIsSuccess(false);
    setError(null);
    reset();
  }, [reset]);

  return {
    // Form state
    isSuccess,
    isSubmitting,
    error,
    errors,
    control,

    // Form handlers
    handleSubmit: handleSubmit(onSubmit),
    setValue,
    resetForm,
    setIsSuccess
  };
}

export default useHipaaForm;
